import {LedgerEntryType, PostingEntryType} from './models/ledgers'
import {autoSpaces, getObjectKey} from '@services/helpers'

export interface IEssentialLink {
  tag?: string,
  title: string,
  route?: RoutesList,
  caption?: string,
  icon: string,
  link?: string,
  onClick?: any,
  isDisabled?: boolean,
  items?: IEssentialLink[]
}

export interface IPageBtns {
  label?: string,
  icon?: string,
  hide?(): boolean
  onClick?(): void
}
export interface IPage {
  title?: string,
  subtitle?: string,
  btns?: IPageBtns[]
}

//---------------------------------------

export interface IWithdraw {
  id: string;
  creationTime: string;
  userID: string;
  currency: string;
  amount: number;
  address: string;
  withdrawTransctionId: string;
  status: string;
}

//---------------------------------------

export enum Currencies {
  BTC = '₿',
  ETH = 'Ξ',
  LTC = 'Ł',
  EUR = '€',
  USD = '$',
}

export enum Position {
  Left = 'Left',
  Right = 'Right'
}

export enum Symbols {
  ETHEUR = 'ETHEUR',
  ETHLTC = 'ETHLTC',
  LTCEUR = 'LTCEUR',
  LTHETH = 'LTHETH',
}

export enum RoutesList {
  preloader = 'preloader',
  auth = 'auth',
  verifications = 'verifications',
  htmlPages = 'htmlPages',
  htmlPage = 'htmlPage',
  cardVerifications = 'cardVerifications',
  cardVerification = 'cardVerification',
  userVerifications = 'userVerifications',
  userVerification = 'userVerification',
  webauthnInit = 'webauthnInit',
  blank = 'blank',
  index = 'index',
  accounts = 'accounts',
  admins = 'admins',
  adminLog = 'adminLog',
  clients = 'clients',
  kytList = 'kytList',
  account = 'account',
  orders = 'orders',
  otcOrders = 'otcOrders',
  matcherOrders = 'matcherOrders',
  positions = 'positions',
  dailyPositions = 'dailyPositions',
  position = 'position',
  order = 'order',
  otcOrder = 'otcOrder',
  trades = 'trades',
  otcTrades = 'otcTrades',
  trade = 'trade',
  otcTrade = 'otcTrade',
  ledger = 'ledger',
  withdrawal = 'withdrawal',
  funding = 'funding',
  clearingHistory = 'clearingHistory',
  clearingDetails = 'clearingDetails',
  depositRecipients = 'depositRecipients',
  withdrowalConfirm = 'withdrowalConfirm',
  marketManagement = 'marketManagement',
  marketManagementSymbol = 'marketManagementSymbol',
  currencies = 'currencies',
  currency = 'currency',
  deposit = 'deposit',
  adminRecord = 'adminRecord',
  settings = 'settings',
  assetProcessing = 'assetProcessing',
  lpSettings = 'lpSettings',
  main = 'main',
  fees = 'fees',
  limits = 'limits',
  quest = 'quest',
  quests = 'quests',
  managementAccounting = 'managementAccounting',
  liquiditySettings = 'liquiditySettings',
  dashboard = 'dashboard',
  clientBalances = 'clientBalances',
  counterparties = 'counterparties',
  exchangersComparison = 'exchangersComparison',
}

export enum PostingReason {
  Order = 'Order', Deposit = 'Deposit', Withdrawl = 'Withdrawl'
}

export function useFilterPostingReason(value: PostingReason) {
  const map = {
    [PostingReason.Deposit]: 'Депозит',
    [PostingReason.Withdrawl]: 'Вывод',
    [PostingReason.Order]: 'Ордер'
  }

  return map[value] || autoSpaces(getObjectKey(value, PostingReason))
}


export enum PostingTypes
{
  Order = 'Order',
  Withdraw = 'Withdraw',
  Deposit = 'Deposit',
  Manual = 'Manual',
  Bonus = 'Bonus'
}

export function useFilterPostingType(value: PostingTypes) {
  const map = {
    [PostingTypes.Deposit]: 'Депозит',
    [PostingTypes.Withdraw]: 'Вывод',
    [PostingTypes.Order]: 'Ордер',
    [PostingTypes.Manual]: 'Ручная проводка',
    [PostingTypes.Bonus]: 'Зачисление бонуса'
  }

  return map[value] || autoSpaces(getObjectKey(value, PostingTypes))
}

export enum BalanceTypes {
  TradeBalance = 'Trade balance',
  HoldOnOrders = 'Hold on orders',
  External = 'External',
  Exchange = 'Exchange',
  TradingFee = 'Trading fee',
  FundingFee = 'Funding fee',
  ReserveBalance = 'Balance reserve',
  HoldOnWithdrawals = 'Hold on withdrawals',
}

export function useFilterBalanceTypes(value: BalanceTypes) {
  const map = {
    [BalanceTypes.TradeBalance]: 'Торговый баланс',
    [BalanceTypes.HoldOnOrders]: 'Холд на ордера',
    [BalanceTypes.HoldOnWithdrawals]: 'Холд на вывод',
    [BalanceTypes.ReserveBalance]: 'К получению',
    [BalanceTypes.TradingFee]: 'Торговая комиссия',
    [BalanceTypes.Exchange]: 'Обмен',
    [BalanceTypes.External]: 'Внешний кошелек',
    [BalanceTypes.FundingFee]: 'Комиссия фандинга'
  }

  return map[value] || autoSpaces(getObjectKey(value, BalanceTypes))
}

export enum BitflowCurrencyType {
  BtcBased = 'BtcBased'
}

export enum ManagementAccountingBalanceTypes {
  ClientsWallet = 'ClientsWallet',
  PendingReceipts = 'PendingReceipts',
  OperationBalance = 'OperationBalance',
  NetworkFee = 'NetworkFee',
  ToBeSend = 'ToBeSend',
  LiquidityProvider = 'LiquidityProvider',
  LiquidityProviderFee = 'LiquidityProviderFee',
  TradeFee = 'TradeFee',
  External = 'External',
  BrokerBalance = 'BrokerBalance',
}

export enum BrokerBalanceTypes {
  ClientsWallet = 'ClientsWallet',
  PendingReceipts = 'PendingReceipts',
  OperationBalance = 'OperationBalance',
  NetworkFee = 'NetworkFee',
  ToBeSend = 'ToBeSend',
  ExternalSource = 'ExternalSource',
  BrokerBalance = 'BrokerBalance',
}

export enum LiquidityProviderBalanceTypes {
  OperationBalance = 'OperationBalance',
  ToBeSend = 'ToBeSend',
  FundingFee = 'FundingFee',
  TradeFee = 'TradeFee',
  External = 'External',
}

export enum LiquidityProviderBalanceTypesShort {
  OperationBalance = 'OperationBalance',
  TradeFee = 'TradeFee',
  External = 'External',
}

export enum ManagementAccountingPostingTypes {
  ClientsDeposit = 'ClientsDeposit',
  ClientsWithdrawal = 'ClientsWithdrawal',
  LiquidityProviderDeposit = 'LiquidityProviderDeposit',
  LiquidityProviderWithdrawal = 'LiquidityProviderWithdrawal',
  Trade = 'Trade',
  TradingFee = 'TradingFee',
  Manual = 'Manual',
}

export enum BrokerPostingTypes {
  ClientsDeposit = 'ClientsDeposit',
  ClientsWithdrawal = 'ClientsWithdrawal',
  Clearing = 'Clearing',
  Manual = 'Manual',
  Trade = 'Trade',
  TradingFee = 'TradingFee',
}

export function useFilterBrokerPostingTypes(value: BrokerPostingTypes) {
  const map = {
    [BrokerPostingTypes.Clearing]: 'Клиринг',
    [BrokerPostingTypes.ClientsDeposit]: 'Депозит клиента',
    [BrokerPostingTypes.ClientsWithdrawal]: 'Вывод клиента',
    [BrokerPostingTypes.Manual]: 'Ручная проводка',
    [BrokerPostingTypes.Trade]: 'Сделка',
    [BrokerPostingTypes.TradingFee]: 'Комиссия сделки'
  }

  return map[value] || autoSpaces(getObjectKey(value, BrokerPostingTypes))
}

export enum USDTGatewayTypes {
  USDT_Trc20 = 'USDT_Trc20'
}

export enum LiquidityProviderPostingTypes {
  LiquidityProviderTrade = 'LiquidityProviderTrade',
  Manual = 'Manual',
}

export enum PositionOwnerDto {
  Broker = 'Broker',
  Kraken = 'Kraken',
  Binance = 'Binance',
  Okex = 'Okex',
  Netex24 = 'Netex24',
  User = 'User',
  Otc = 'Otc',
}

export enum QuestStatuses {
  InProgress = 'InProgress',
  AdminRequired = 'AdminRequired',
  Success = 'Success',
  Fail = 'Fail',
  Expired = 'Expired',
}

export function useFilterQuestStatuses(value: QuestStatuses) {
  const map = {
    [QuestStatuses.InProgress]: 'В процессе',
    [QuestStatuses.AdminRequired]: 'Необходимо участие администратора',
    [QuestStatuses.Success]: 'Успешно',
    [QuestStatuses.Fail]: 'Не пройден',
    [QuestStatuses.Expired]: 'Истек'
  }

  return map[value] || autoSpaces(getObjectKey(value, QuestStatuses))
}

export enum QuestTypes {
  Login = 'Login',
  PasswordRecovery = 'PasswordRecovery',
  LostTwoFaCode = 'LostTwoFaCode',
  LostTwoFaCodeSupport = 'LostTwoFaCodeSupport',
  ChangePassword = 'ChangePassword',
  AccountUnblockSupport = 'AccountUnblockSupport',
  IpBypassSupport = 'IpBypassSupport',
  EmailChange = 'EmailChange',
}

export enum QuestTypesFilter {
  Login = 'Login',
  PasswordRecovery = 'PasswordRecovery',
  EmailChange = 'EmailChange',
  LostTwoFaCodeSupport = 'LostTwoFaCodeSupport',
  AccountUnblockSupport = 'AccountUnblockSupport',
  IpBypassSupport = 'IpBypassSupport',
}

export function useFilterQuestTypesFilter(value: QuestTypesFilter) {
  const map = {
    [QuestTypesFilter.AccountUnblockSupport]: 'Разблокировка аккаунта через поддержку',
    [QuestTypesFilter.EmailChange]: 'Смена e-mail',
    [QuestTypesFilter.IpBypassSupport]: 'Обход белого списка IP адресов',
    [QuestTypesFilter.Login]: 'Вход',
    [QuestTypesFilter.LostTwoFaCodeSupport]: 'Потерянный код 2FA',
    [QuestTypesFilter.PasswordRecovery]: 'Восстановление пароля'
  }

  return map[value] || autoSpaces(getObjectKey(value, QuestTypesFilter))
}


export enum QuestEvents {
  /// <summary>
  /// Начало квеста.
  /// </summary>
  Start = 'Start',
  /// <summary>
  /// Требуется Captcha.
  /// </summary>
  CaptchaRequired = 'CaptchaRequired',
  /// <summary>
  /// Недопустимый IP.
  /// </summary>
  InvalidIp = 'InvalidIp',
  /// <summary>
  /// неправильный пароль.
  /// </summary>
  InvalidPassword = 'InvalidPassword',
  /// <summary>
  /// Неправильный 2FA код.
  /// </summary>
  InvalidTwoFa = 'InvalidTwoFa',
  /// <summary>
  /// Пользователь заблокирован.
  /// </summary>
  UserBlocked = 'UserBlocked',
  /// <summary>
  /// Пользователь не подтвержден.
  /// </summary>
  UserNotConfirmed = 'UserNotConfirmed',
  /// <summary>
  /// Подтверждение отпечатка.
  /// </summary>
  ApprovalEmailSent = 'ApprovalEmailSent',
  /// <summary>
  /// Заблокирован пользователем.
  /// </summary>
  BlockedByUser = 'BlockedByUser',
  /// <summary>
  /// Закрыт.
  /// </summary>
  Closed = 'Closed',
  /// <summary>
  /// Пароль изменен.
  /// </summary>
  PasswordChanged = 'PasswordChanged',
  /// <summary>
  /// Таймаут.
  /// </summary>
  Timeout = 'Timeout',
  /// <summary>
  /// Код смс введен верно.
  /// </summary>
  SmsConfirmed = 'SmsConfirmed',
  /// <summary>
  /// Загрузка документов из тикета.
  /// </summary>
  SupportVerification = 'SupportVerification',
  /// <summary>
  /// Отправлена ссылка ByPass TwoFa.
  /// </summary>
  SupportByPassLinkSent = 'SupportByPassLinkSent',
  /// <summary>
  /// Заблокирован админом.
  /// </summary>
  BlockedByAdmin = 'BlockedByAdmin',
  /// <summary>
  /// Отправлена ссылка на смену пароля.
  /// </summary>
  EmailSent = 'EmailSent',
  /// <summary>
  /// Код смс не валиден.
  /// </summary>
  SmsInvalid = 'SmsInvalid',
  /// <summary>
  /// Sms отправлено.
  /// </summary>
  SmsSent = 'SmsSent',
  /// <summary>
  /// Ошибка при оправке sms.
  /// </summary>
  SmsSendingFailed = 'SmsSendingFailed',
  /// <summary>
  /// Верный 2fa код.
  /// </summary>
  TwoFaConfirmed = 'TwoFaConfirmed',
  /// <summary>
  /// Пароль потвержден.
  /// </summary>
  PasswordConfirmed = 'PasswordConfirmed',
  /// <summary>
  /// Ссылка отправлена.
  /// </summary>
  LinkSent = 'LinkSent',
  /// <summary>
  /// Новое устройство подтверждено.
  /// </summary>
  NewDeviceConfirmed = 'NewDeviceConfirmed',
  /// <summary>
  /// Неверный код из письма.
  /// </summary>
  InvalidEmailCode = 'InvalidEmailCode',
  /// <summary>
  /// Верификация запрошена.
  /// </summary>
  Verification = 'Verification',
  /// <summary>
  /// Документы верификации получены.
  /// </summary>
  DocumentsReceived = 'DocumentsReceived',
  /// <summary>
  /// Верификация начата пользователем.
  /// </summary>
  VerificationStarted = 'VerificationStarted',
  /// <summary>
  /// Верификация перезапущена оператором.
  /// </summary>
  VerificationRetry = 'VerificationRetry',
}

export function useFilterQuestEvents(value: QuestEvents) {
  const map = {
    [QuestEvents.Start]: 'Начало квеста',
    [QuestEvents.CaptchaRequired]: 'Требуется Captcha',
    [QuestEvents.InvalidIp]: 'Неразрешенный IP адрес',
    [QuestEvents.InvalidPassword]: 'Неправильный пароль',
    [QuestEvents.InvalidTwoFa]: 'Неправильный 2FA код',
    [QuestEvents.UserBlocked]: 'Пользователь заблокирован',
    [QuestEvents.UserNotConfirmed]: 'Пользователь не подтвержден',
    [QuestEvents.ApprovalEmailSent]: 'Подтверждение отпечатка',
    [QuestEvents.BlockedByUser]: 'Заблокирован пользователем',
    [QuestEvents.Closed]: 'Квест закрыт',
    [QuestEvents.PasswordChanged]: 'Пароль изменен',
    [QuestEvents.Timeout]: 'Квест закрыт по таймауту',
    [QuestEvents.SmsConfirmed]: 'Код смс введен верно',
    [QuestEvents.SupportVerification]: 'Загрузка документов из тикета',
    [QuestEvents.SupportByPassLinkSent]: 'Отправлена ссылка ByPass TwoFa',
    [QuestEvents.BlockedByAdmin]: 'Заблокирован админом',
    [QuestEvents.EmailSent]: 'Отправлена ссылка на смену пароля',
    [QuestEvents.SmsInvalid]: 'Код смс не валиден',
    [QuestEvents.SmsSent]: 'Sms отправлено',
    [QuestEvents.SmsSendingFailed]: 'Ошибка при отправке sms',
    [QuestEvents.TwoFaConfirmed]: 'Верный 2fa код',
    [QuestEvents.PasswordConfirmed]: 'Пароль подтвержден',
    [QuestEvents.LinkSent]: 'Ссылка отправлена',
    [QuestEvents.NewDeviceConfirmed]: 'Новое устройство подтверждено',
    [QuestEvents.InvalidEmailCode]: 'Неверный код из письма',
    [QuestEvents.Verification]: 'Верификация запрошена',
    [QuestEvents.DocumentsReceived]: 'Документы верификации получены',
    [QuestEvents.VerificationStarted]: 'Верификация начата пользователем',
    [QuestEvents.VerificationRetry]: 'Верификация перезапущена оператором'
  }

  return map[value] || autoSpaces(getObjectKey(value, QuestEvents))
}


export enum WithdrawalStatus {
  Initialized = 'Initialized',
  Processing = 'Processing',
  Confirmed = 'Confirmed',
  Cancelled = 'Cancelled',
  AddedManually = 'AddedManually',
  MemPool = 'MemPool',
  BalancesReconciliation = 'BalancesReconciliation',
  Waiting = 'Waiting'
}

export enum VerificationStatuses {
  NotVerified = 'NotVerified',
  PersonalInfo = 'PersonalInfo',
  Documents = 'Documents',
  Selfie = 'Selfie',
  Waiting = 'Waiting',
  Retry = 'Retry',
  ManualCheck = 'ManualCheck',
  Failed = 'Failed',
  Completed = 'Completed',
  ResetManually = 'ResetManually',
}

export enum UsedVerificationStatuses {
  NotVerified = 'NotVerified',
  Waiting = 'Waiting',
  Failed = 'Failed',
  Completed = 'Completed'
}

export enum CardBanks {
  Unknown = 'Unknown',
  Other = 'Other',
  Sber = 'Sber',
  Tinkoff = 'Tinkoff',
  Rosbank = 'Rosbank',
  Raiffeisen = 'Raiffeisen',
  Alfa = 'Alfa',
  Promsvyazbank = 'Promsvyazbank',
  Vtb = 'Vtb',
  RussianStandard = 'RussianStandard',
}

export enum CardVerificationStatusesFilter {
  New = 'New',
  Checking = 'Checking',
  Rejected = 'Rejected',
  Verified = 'Verified',
  Retry = 'Retry',
  Removed = 'Removed',
  Blocked = 'Blocked',
}

export enum VerificationStatusesFilter {
  PersonalInfo = 'PersonalInfo',
  Documents = 'Documents',
  Selfie = 'Selfie',
  Waiting = 'Waiting',
  Retry = 'Retry',
  ManualCheck = 'ManualCheck',
  Failed = 'Failed',
  Completed = 'Completed',
  ResetManually = 'ResetManually',
}

export enum VerificationActors {
  Client = 'Client',
  Admin = 'Admin',
  SumSub = 'SumSub'
}

export enum SymbolStatus {
  Enabled = 'Enabled',
  DisabledByOperator = 'DisabledByOperator',
  DisabledBySystem = 'DisabledBySystem',
  Disabled = 'Disabled',
}

export function useFilterStatusSymbol(value: SymbolStatus) {
  const map = {
    [SymbolStatus.Disabled]: 'Отключена',
    [SymbolStatus.DisabledByOperator]: 'Отключена оператором',
    [SymbolStatus.DisabledBySystem]: 'Отключена системой',
    [SymbolStatus.Enabled]: 'Включена',
  }

  return map[value] || autoSpaces(getObjectKey(value, SymbolStatus))
}

export enum SymbolStatusFilter {
  Enabled = 'Enabled',
  DisabledByOperator = 'DisabledByOperator',
  DisabledBySystem = 'DisabledBySystem',
  Disabled = 'Disabled',
}

export enum DocumentType {
  Passport = 'Passport',
  DrivingLicence = 'DrivingLicence',
  IdCard = 'IdCard',
}

export enum IdentificationTypes {
  IdentityPrimary = 'IdentityPrimary',
  SumSubPrimary = 'SumSubPrimary',
  KycPlatform = 'KycPlatform',
  Manually = 'Manually'
}

export enum PaymentCardStatusesDto {
  New = 'New',
  Checking = 'Checking',
  Verified = 'Verified',
  Retry = 'Retry',
  Rejected = 'Rejected',
  Blocked = 'Blocked',
  Removed = 'Removed',
}

export enum LiquidityProviders {
  Binance = 'Binance',
  Okex = 'Okex',
  Netex24 = 'Netex24',
  BaseRates = 'BaseRates',
  Garantex = 'Garantex',
  All = 'All',
  NetexTrade = 'Trade',
  Cross = 'Cross',
  Bitfinex = 'Bitfinex',
  ProviderEmulator1 = 'ProviderEmulator1',
  ProviderEmulator2 = 'ProviderEmulator2',
  ProviderEmulator3 = 'ProviderEmulator3',
}

export enum ApiKeyPermissions {
  UserData = 'UserData',
  Trade = 'Trade',
  Codes = 'Codes',
  Funding = 'Funding',
}

export enum DepositRecipientLedgerTypes {
  Deposit = 'Deposit',
  Clearing = 'Clearing',
  Withdrawal = 'Withdrawal',
}

export enum KytService {
  Scorechain = 'Scorechain',
  AmlBot = 'AmlBot',
  Shard = 'Shard',
}

export enum PolicyDirection
{
  Deposit = 'Deposit',
  Withdrawal = 'Withdrawal'
}

export function useFilterPolicyDirection(value: PolicyDirection) {
  const map = {
    [PolicyDirection.Deposit]: 'Депозит',
    [PolicyDirection.Withdrawal]: 'Вывод',
  }

  return map[value] || autoSpaces(getObjectKey(value, PolicyDirection))
}
